import { getCookie, removeAuthCookie, setAuthCookie } from '@/helpers/helpers'

// The support user's / admin's own session is parked in a second pair of auth cookies while they
// impersonate someone. Same domain scope as `token`/`role`, so it is shared by every tab exactly
// like the session itself.
const IMPERSONATOR_TOKEN_COOKIE = 'impersonatorToken'
const IMPERSONATOR_ROLE_COOKIE = 'impersonatorRole'

export type AuthCredentials = {
  token: string
  role: string
}

// Mirrors what `getUserToken` (POST /session) writes into the `role` cookie.
export const toAuthRoleCookie = (role: string) => (role === 'Administrator' ? 'admin' : role)

export const isImpersonating = () => Boolean(getCookie(IMPERSONATOR_TOKEN_COOKIE))

export const clearImpersonation = () => {
  removeAuthCookie(IMPERSONATOR_TOKEN_COOKIE)
  removeAuthCookie(IMPERSONATOR_ROLE_COOKIE)
}

/**
 * Parks the current session and swaps in the impersonated user's credentials. Nested impersonation
 * is not allowed — the original session must be restored first, otherwise it would be overwritten.
 */
export const startImpersonation = ({ token, role }: AuthCredentials) => {
  if (isImpersonating()) {
    throw new Error('Already impersonating — end the current impersonation first')
  }

  setAuthCookie(IMPERSONATOR_TOKEN_COOKIE, getCookie('token'))
  setAuthCookie(IMPERSONATOR_ROLE_COOKIE, getCookie('role'))
  setAuthCookie('token', token)
  setAuthCookie('role', toAuthRoleCookie(role))
}

/** Restores the parked support/admin session. Returns false when there was nothing to restore. */
export const endImpersonation = () => {
  const token = getCookie(IMPERSONATOR_TOKEN_COOKIE)
  const role = getCookie(IMPERSONATOR_ROLE_COOKIE)
  if (!token || !role) {
    clearImpersonation()
    return false
  }

  setAuthCookie('token', token)
  setAuthCookie('role', role)
  clearImpersonation()
  return true
}

// Why the session just switched back to the support account. Survives the hard reload below so the
// app can toast about it; per-tab sessionStorage, as only the reloading tab should show it.
const IMPERSONATION_NOTICE_KEY = 'impersonation-notice'

export type ImpersonationNotice = 'ended' | 'expired'

export const setImpersonationNotice = (notice: ImpersonationNotice) => {
  try {
    sessionStorage.setItem(IMPERSONATION_NOTICE_KEY, notice)
  } catch {
    // Storage unavailable (private mode, blocked site data) — the toast is a nicety, skip it.
  }
}

/** Returns the pending notice once and clears it. */
export const consumeImpersonationNotice = (): ImpersonationNotice | null => {
  try {
    const notice = sessionStorage.getItem(IMPERSONATION_NOTICE_KEY)
    sessionStorage.removeItem(IMPERSONATION_NOTICE_KEY)
    return notice === 'ended' || notice === 'expired' ? notice : null
  } catch {
    return null
  }
}

/**
 * Hard reload at the given hash route. Switching accounts must reset everything held in memory
 * (Redux userData, React Query cache, analytics identity), same as a fresh login.
 */
export const reloadAt = (path: string) => {
  window.location.replace(`${window.location.pathname}#${path}`)
  window.location.reload()
}
